import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useCancellationRollupReport,
    useExportCancellationRollup,
    type CancellationRollupFilter,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useCancelReasons } from '@/api/cancelReasons';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Cancellation Rollup — reason × payer × month. Same underlying rows as the
 * Session Activity report but shaped for pattern-spotting over time (billing/
 * ops audience). Payer attribution uses the client's current primary payer.
 */

export function CancellationRollupPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<CancellationRollupFilter>({});
    const [draft, setDraft] = useState<CancellationRollupFilter>({});

    const { data, isLoading, isError, error, isFetching } = useCancellationRollupReport(filter);
    const exportM = useExportCancellationRollup();
    const clientsQuery = useClientNames();
    const reasonsQuery = useCancelReasons();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const reasonOptions = useMemo(() => {
        if (!reasonsQuery.data) return [];
        return reasonsQuery.data
            .filter((r) => r.active)
            .map((r) => ({ id: r.id, label: r.text }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [reasonsQuery.data]);

    // Payer options derive from the latest rollup response so the dropdown
    // only lists payers that actually have cancellations in scope. Empty on
    // first load — the user applies a broader date range to populate it.
    const payerOptions = useMemo(() => {
        if (!data?.byPayer) return [];
        return data.byPayer
            .map((p) => p.payer)
            .filter((p) => p && p !== 'Unassigned')
            .sort((a, b) => a.localeCompare(b));
    }, [data?.byPayer]);

    function apply() { setFilter({ ...draft }); }
    function clear() {
        setDraft({});
        setFilter({});
    }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError renders below */ }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Cancellation Rollup</h1>
                    <p className="text-xs text-text-subtle">
                        Cancellations grouped by reason, payer, and month. Payer comes from the client's current primary insurance.
                    </p>
                </div>
                {canExport && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={runExport}
                        disabled={exportM.isPending || isLoading}
                    >
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <SelectField label="Client" value={draft.clientId} options={clientOptions}
                            onChange={(v) => setDraft({ ...draft, clientId: v })} />
                        <SelectField label="Cancel reason" value={draft.cancelReasonId} options={reasonOptions}
                            onChange={(v) => setDraft({ ...draft, cancelReasonId: v })} />
                        <label className="text-xs block">
                            <span className="text-text-muted">Payer</span>
                            <select
                                value={draft.payerName ?? ''}
                                onChange={(e) => setDraft({ ...draft, payerName: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All payers</option>
                                {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Date to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <Kpi label="Total cancellations" value={data.kpis.total.toLocaleString()} />
                        <Kpi label="Months with activity" value={data.kpis.months.toLocaleString()} />
                        <Kpi label="Distinct reasons" value={data.kpis.reasons.toLocaleString()} />
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Reason × Month</CardTitle></CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.matrix.reasons.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No cancellations match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Reason</th>
                                            {data.matrix.months.map((m) => (
                                                <th key={m} className="px-3 py-2 font-bold text-right">{m}</th>
                                            ))}
                                            <th className="px-3 py-2 font-bold text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.matrix.reasons.map((r) => {
                                            const total = data.matrix.months.reduce((n, m) => n + (r.counts[m] || 0), 0);
                                            return (
                                                <tr key={r.cancelReasonId ?? r.label} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">{r.label}</td>
                                                    {data.matrix.months.map((m) => (
                                                        <td key={m} className="px-3 py-2 text-right tabular-nums">
                                                            {r.counts[m] ? r.counts[m] : '—'}
                                                        </td>
                                                    ))}
                                                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{total}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader><CardTitle>By reason</CardTitle></CardHeader>
                            <CardBody className="pt-0">
                                {data.byReason.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No cancellations.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Reason</th>
                                                <th className="px-3 py-2 font-bold text-right">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.byReason.map((r) => (
                                                <tr key={r.cancelReasonId ?? r.label} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">{r.label}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>By payer</CardTitle></CardHeader>
                            <CardBody className="pt-0">
                                {data.byPayer.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No cancellations.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Payer</th>
                                                <th className="px-3 py-2 font-bold text-right">Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.byPayer.map((p) => (
                                                <tr key={p.payer} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">{p.payer}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{p.count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader><CardTitle>By month</CardTitle></CardHeader>
                        <CardBody className="pt-0">
                            {data.byMonth.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No cancellations.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Month</th>
                                            <th className="px-3 py-2 font-bold text-right">Cancellations</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byMonth.map((m) => (
                                            <tr key={m.month} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{m.month}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{m.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function SelectField({
    label, value, options, onChange,
}: {
    label: string;
    value: string | undefined;
    options: { id: string; label: string }[];
    onChange: (v: string | undefined) => void;
}) {
    return (
        <label className="text-xs block">
            <span className="text-text-muted">{label}</span>
            <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
            >
                <option value="">All</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className="font-display text-xl font-bold mt-1 tabular-nums text-text">{value}</p>
            </CardBody>
        </Card>
    );
}
